import type { RowData } from "@tanstack/react-table";
import { type JSX, memo, useMemo } from "react";
import clsx from "clsx";

import classes from "../../styles.module.scss";

import type { ITableBodyProps } from "@components/organisms/pageTable";
import { Table } from "@components/organisms/table";
import { TableNoData } from "@components/atoms/tableNoData";
import { BaseLoader } from "@components/molecules/baseLoader";

export const TableBody = memo(function TableBody<TData extends RowData>({
  type,
  tableProps,
  children,
  total = 0,
  isLoading,
  isError,
  loaderType,
  isFetching,
}: ITableBodyProps<TData>) {
  const isBasic = useMemo(() => type === "basic", [type]);
  const isCustom = useMemo(() => type === "custom", [type]);

  return (
    <BaseLoader
      isLoading={isLoading || !!isFetching}
      type={loaderType || (isBasic ? "basic-table" : "appointment-card")}
      isError={isError}
    >
      {isBasic && tableProps && (
        <Table
          showRow={false}
          hasActionCell={tableProps.hasActionCell}
          columnDef={tableProps.columnDef}
          dataJSON={tableProps.dataJSON}
          onRowSelectionChange={tableProps.onRowSelectionChange}
          thead={tableProps.thead}
          tableClassName={tableProps.tableClassName}
          tableContainerClassName={tableProps.tableContainerClassName}
        />
      )}
      {!isBasic &&
        !isCustom &&
        (total > 0 ? (
          <div
            className={clsx(
              type === "row" && classes.pageTable__gridRow,
              type === "grid",
            )}
          >
            {children}
          </div>
        ) : (
          <TableNoData />
        ))}
      {!isBasic && isCustom && (total > 0 ? <>{children}</> : <TableNoData />)}
    </BaseLoader>
  );
}) as <TData extends RowData>(props: ITableBodyProps<TData>) => JSX.Element;
